import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import RoomServiceRoundedIcon from '@mui/icons-material/RoomServiceRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import SaveIcon from '@mui/icons-material/Save'

import CateringBandEditor, { bandsToRows, parseBandRows } from '../components/Catering/CateringBandEditor'
import { deliveryModeLabel } from '../utils/catering'
import { cateringTheme, CT } from './cateringTheme'

/**
 * Super admin: catering fees for the whole platform (backend migration 131).
 * The platform catering defaults and RushMyFood's catering delivery tiers,
 * then one row per restaurant for its own overrides. Anything blank falls
 * back a level: a restaurant's blank → the platform catering default → the
 * regular fee or commission.
 */

const CONFIG_FIELDS = 'serviceFee deliveryCommissionRate selfDeliveryCommissionRate pickupCommissionRate searchRadiusMiles bands { _id maxMiles fee sortOrder }'
const FEE_FIELDS = 'restaurantId restaurantName hasCatering deliveryMode serviceFee deliveryCommissionRate selfDeliveryCommissionRate pickupCommissionRate'

const GET_CONFIG = gql`
  query CateringConfig {
    cateringConfig { ${CONFIG_FIELDS} }
  }
`
const GET_FEES = gql`
  query CateringRestaurantFees {
    cateringRestaurantFees { ${FEE_FIELDS} }
  }
`
const SAVE_CONFIG = gql`
  mutation SaveCateringConfig($input: CateringConfigInput!, $bands: [CateringBandInput!]!) {
    saveCateringConfig(input: $input, bands: $bands) { ${CONFIG_FIELDS} }
  }
`
const SAVE_FEES = gql`
  mutation SaveRestaurantCateringFees($restaurantId: ID!, $input: CateringFeesInput!) {
    saveRestaurantCateringFees(restaurantId: $restaurantId, input: $input) { ${FEE_FIELDS} }
  }
`

// The three commission scenarios, worded like Commission Rates.
const RATES = [
  { key: 'deliveryCommissionRate', label: 'We deliver' },
  { key: 'selfDeliveryCommissionRate', label: 'Restaurant delivers' },
  { key: 'pickupCommissionRate', label: 'Pickup' }
]
const FEE_KEYS = ['serviceFee', ...RATES.map(r => r.key)]

const str = v => (v == null ? '' : String(v))
const configForm = c => ({
  serviceFee: str(c.serviceFee),
  deliveryCommissionRate: str(c.deliveryCommissionRate),
  selfDeliveryCommissionRate: str(c.selfDeliveryCommissionRate),
  pickupCommissionRate: str(c.pickupCommissionRate),
  searchRadiusMiles: str(c.searchRadiusMiles)
})
const feeForm = r => Object.fromEntries(FEE_KEYS.map(k => [k, str(r[k])]))
const sameValues = (a, b, keys) => keys.every(k => String(a[k]).trim() === String(b[k]).trim())

/**
 * One fee box → its value: blank = null (fall back a level), else a number in
 * range. Returns { value } or { error }.
 */
const readAmount = (raw, label, max = 100) => {
  const s = String(raw).trim()
  if (s === '') return { value: null }
  const n = Number(s)
  if (!Number.isFinite(n) || n < 0 || n > max) return { error: `${label} must be between 0 and ${max}.` }
  return { value: n }
}

const readFees = (f, labels) => {
  const out = {}
  for (const k of FEE_KEYS) {
    const r = readAmount(f[k], labels[k])
    if (r.error) return { error: r.error }
    out[k] = r.value
  }
  return { input: out }
}
const FEE_LABELS = {
  serviceFee: 'Catering service fee',
  deliveryCommissionRate: 'Catering % (we deliver)',
  selfDeliveryCommissionRate: 'Catering % (restaurant delivers)',
  pickupCommissionRate: 'Catering % (pickup)'
}

// ---- Presentation (the restaurant Catering page's surfaces) ----
const PAGE = {
  bgcolor: CT.page, color: CT.text, fontFamily: CT.font, fontSize: 15, lineHeight: 1.45,
  minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }
}
const CONTENT = {
  maxWidth: 1180, mx: 'auto', boxSizing: 'content-box',
  px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 }
}
const CARD = { bgcolor: CT.card, border: `1px solid ${CT.border}`, borderRadius: '16px', boxShadow: 'none', overflow: 'hidden' }
const CARD_TITLE = { fontSize: 20, fontWeight: 700, lineHeight: 1.3, color: CT.text }
const BODY = { fontSize: 15, lineHeight: 1.5, color: CT.body, '& strong': { fontWeight: 700, color: CT.text } }
const TILE = { display: 'grid', placeItems: 'center', flexShrink: 0 }
const GROUP_LABEL = { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: CT.secondary, mb: 1.25 }
const NUM = { '& .MuiOutlinedInput-input': { fontVariantNumeric: 'tabular-nums' } }
const ROW_FIELD = { ...NUM, width: 168, '& .MuiInputLabel-root': { fontSize: 12.5 } }

const dollar = <InputAdornment position="start">$</InputAdornment>
const percent = <InputAdornment position="end">%</InputAdornment>

function SectionHead({ id, icon, title, intro }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Box aria-hidden sx={{ ...TILE, width: 44, height: 44, borderRadius: '12px', bgcolor: CT.tileBg, color: CT.icon, '& svg': { fontSize: 22 } }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, pt: '2px' }}>
        <Typography component="h2" id={id} sx={CARD_TITLE}>{title}</Typography>
        {intro && <Typography sx={{ ...BODY, mt: 0.5, maxWidth: 760 }}>{intro}</Typography>}
      </Box>
    </Box>
  )
}

// One restaurant: its catering status, then its four override boxes and Save.
function RestaurantRow({ row, config, onSaved }) {
  const [form, setForm] = useState(() => feeForm(row))
  const [notif, setNotif] = useState(null) // { severity, msg }
  const [mutSave, { loading }] = useMutation(SAVE_FEES)

  useEffect(() => { setForm(feeForm(row)) }, [row])

  const dirty = !sameValues(form, feeForm(row), FEE_KEYS)
  const fallback = k => (config && config[k] != null && config[k] !== ''
    ? `Default ${k === 'serviceFee' ? Number(config[k]).toFixed(2) : config[k]}`
    : 'Regular')

  const show = (severity, msg) => {
    setNotif({ severity, msg })
    setTimeout(() => setNotif(null), 4000)
  }

  const save = async () => {
    const parsed = readFees(form, FEE_LABELS)
    if (parsed.error) { show('error', parsed.error); return }
    try {
      const res = await mutSave({ variables: { restaurantId: row.restaurantId, input: parsed.input } })
      onSaved(res.data.saveRestaurantCateringFees)
      show('success', 'Saved')
    } catch (err) {
      show('error', err.message || 'Could not save')
    }
  }

  return (
    <Box component="li" sx={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', columnGap: 3, rowGap: 2,
      px: { xs: 2.5, sm: 4 }, py: 2.5, bgcolor: dirty ? CT.noteBg : 'transparent', transition: 'background-color 150ms ease',
      '&:not(:last-of-type)': { borderBottom: `1px solid ${CT.rowBorder}` }
    }}>
      <Box sx={{ flex: '1 1 220px', minWidth: 0, alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box aria-hidden sx={{ ...TILE, width: 36, height: 36, borderRadius: '10px', bgcolor: CT.tileBg, color: CT.icon }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: CT.text, overflowWrap: 'anywhere' }}>{row.restaurantName || `Restaurant ${row.restaurantId}`}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <Box component="span" sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 22, px: 1, borderRadius: '6px',
              fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
              ...(row.hasCatering
                ? { bgcolor: CT.accentSoft, color: CT.accent, border: `1px solid ${CT.accentBorder}` }
                : { bgcolor: CT.chipBg, color: CT.secondary, border: `1px solid ${CT.border}` })
            }}>
              <RoomServiceRoundedIcon aria-hidden sx={{ fontSize: 14 }} />
              {row.hasCatering ? 'Catering on' : 'Catering off'}
            </Box>
            <Typography component="span" sx={{ fontSize: 13, color: CT.secondary }}>{deliveryModeLabel(row.deliveryMode, 'admin')}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
        <TextField label="Service fee" type="number" size="small" value={form.serviceFee}
          onChange={e => setForm(f => ({ ...f, serviceFee: e.target.value }))}
          placeholder={fallback('serviceFee')} inputProps={{ min: 0, max: 100, step: 0.01 }}
          InputProps={{ startAdornment: dollar }} sx={ROW_FIELD} />
        {RATES.map(r => (
          <TextField key={r.key} label={r.label} type="number" size="small" value={form[r.key]}
            onChange={e => setForm(f => ({ ...f, [r.key]: e.target.value }))}
            placeholder={fallback(r.key)} inputProps={{ min: 0, max: 100, step: 0.5 }}
            InputProps={{ endAdornment: percent }} sx={ROW_FIELD} />
        ))}
        <Button variant="contained" onClick={save} disabled={!dirty || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ height: 48 }}>
          Save
        </Button>
      </Box>

      {notif && (
        <Alert severity={notif.severity} sx={{ width: '100%', py: 0 }}>{notif.msg}</Alert>
      )}
    </Box>
  )
}

export default function SuperAdminCatering() {
  const [form, setForm] = useState(null)
  const [savedForm, setSavedForm] = useState(null)
  const [rows, setRows] = useState([])
  const [savedRows, setSavedRows] = useState('[]')
  const [errors, setErrors] = useState({})
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' })
  const [feeRows, setFeeRows] = useState([])
  const [search, setSearch] = useState('')
  const [cateringOnly, setCateringOnly] = useState(false)

  const { data: configData, loading: configLoading, error: configError } = useQuery(GET_CONFIG, { fetchPolicy: 'network-only' })
  const { data: feesData, loading: feesLoading, error: feesError } = useQuery(GET_FEES, { fetchPolicy: 'network-only' })
  const [mutConfig, { loading: savingConfig }] = useMutation(SAVE_CONFIG)

  const loadConfig = c => {
    const f = configForm(c)
    setForm(f)
    setSavedForm(f)
    const r = bandsToRows(c.bands)
    setRows(r)
    setSavedRows(JSON.stringify(r))
  }

  useEffect(() => { if (configData?.cateringConfig) loadConfig(configData.cateringConfig) }, [configData])
  useEffect(() => { if (feesData?.cateringRestaurantFees) setFeeRows(feesData.cateringRestaurantFees) }, [feesData])

  const configKeys = ['serviceFee', ...RATES.map(r => r.key), 'searchRadiusMiles']
  const configDirty = !!form && !!savedForm && (!sameValues(form, savedForm, configKeys) || JSON.stringify(rows) !== savedRows)
  // The saved platform defaults, for the restaurant rows' placeholders.
  const platform = savedForm

  const update = (k, v) => {
    setForm(f => ({ ...f, [k]: v }))
    setErrors(e => ({ ...e, [k]: '' }))
  }

  const saveConfig = async () => {
    const e = {}
    const input = {}
    for (const k of FEE_KEYS) {
      const r = readAmount(form[k], FEE_LABELS[k])
      if (r.error) e[k] = r.error
      else input[k] = r.value
    }
    const radius = Number(String(form.searchRadiusMiles).trim())
    if (String(form.searchRadiusMiles).trim() === '' || !Number.isFinite(radius) || radius < 1 || radius > 100) {
      e.searchRadiusMiles = 'Enter a radius from 1 to 100 miles.'
    } else {
      input.searchRadiusMiles = radius
    }
    const parsed = parseBandRows(rows)
    if (parsed.error) e.bands = parsed.error
    setErrors(e)
    if (Object.values(e).some(Boolean)) {
      setSnack({ open: true, severity: 'error', msg: 'Check the highlighted fields.' })
      return
    }
    try {
      const res = await mutConfig({ variables: { input, bands: parsed.bands } })
      loadConfig(res.data.saveCateringConfig)
      setSnack({ open: true, severity: 'success', msg: 'Platform catering defaults saved' })
    } catch (err) {
      setSnack({ open: true, severity: 'error', msg: err.message || 'Could not save' })
    }
  }

  const discardConfig = () => {
    setForm(savedForm)
    setRows(JSON.parse(savedRows))
    setErrors({})
  }

  const onRowSaved = saved => setFeeRows(list => list.map(r => (String(r.restaurantId) === String(saved.restaurantId) ? saved : r)))

  const withCatering = feeRows.filter(r => r.hasCatering).length
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return feeRows.filter(r =>
      (!cateringOnly || r.hasCatering) &&
      (!q || String(r.restaurantName || '').toLowerCase().includes(q)))
  }, [feeRows, search, cateringOnly])

  const configField = (k, label, adornment, extra = {}) => (
    <TextField label={label} type="number" size="small" value={form[k]}
      onChange={e => update(k, e.target.value)}
      error={!!errors[k]} helperText={errors[k] || extra.helperText || ' '}
      placeholder={extra.placeholder ?? 'Same as regular'}
      inputProps={{ min: 0, max: 100, step: extra.step || 0.01 }}
      InputProps={adornment === '$' ? { startAdornment: dollar } : adornment === '%' ? { endAdornment: percent } : { endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
      fullWidth sx={NUM} />
  )

  return (
    <ThemeProvider theme={cateringTheme}>
      <Box sx={PAGE}>
        <Box sx={{ ...CONTENT, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header */}
          <Box component="header" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box aria-hidden sx={{ ...TILE, width: 52, height: 52, borderRadius: '14px', bgcolor: CT.accentSoft, color: CT.accent, border: `1px solid ${CT.accentBorder}` }}>
              <RoomServiceRoundedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: CT.text }}>Catering</Typography>
              <Typography sx={{ fontSize: 15, color: CT.secondary, mt: 0.5 }}>
                What catering orders cost across the platform, the catering search radius, and each restaurant&apos;s overrides
              </Typography>
            </Box>
          </Box>

          {configError && <Alert severity="error">{configError.message}</Alert>}
          {configLoading && !form && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          )}

          {form && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Platform catering defaults */}
              <Paper component="section" aria-labelledby="catering-defaults" elevation={0} sx={{ ...CARD, p: { xs: 2.5, sm: '28px 32px 24px' } }}>
                <SectionHead id="catering-defaults" icon={<TuneRoundedIcon />} title="Platform catering defaults"
                  intro={<>Used for every catering order unless the restaurant has its own below. Leave a box blank and catering uses the <strong>regular</strong> fee or commission.</>} />

                <Box sx={{ display: 'grid', gap: 3, mt: 3, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2.4fr)' } }}>
                  <Box>
                    <Typography sx={GROUP_LABEL}>Customer pays</Typography>
                    {configField('serviceFee', 'Catering service fee', '$', { helperText: 'Flat fee on a catering card order.' })}
                  </Box>
                  <Box>
                    <Typography sx={GROUP_LABEL}>Catering commission (% of subtotal)</Typography>
                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' } }}>
                      {RATES.map(r => <Box key={r.key}>{configField(r.key, r.label, '%', { step: 0.5 })}</Box>)}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mt: 2, maxWidth: { md: 'calc((100% - 24px) / 3.4)' } }}>
                  <Typography sx={GROUP_LABEL}>Storefront</Typography>
                  {configField('searchRadiusMiles', 'Catering search radius', 'mi', {
                    placeholder: '25', step: 1,
                    helperText: 'The Catering list shows restaurants with catering within this distance. 1–100 miles.'
                  })}
                </Box>
              </Paper>

              {/* RushMyFood catering delivery tiers */}
              <Paper component="section" aria-label="RushMyFood catering delivery fee" elevation={0} sx={CARD}>
                <CateringBandEditor
                  rows={rows}
                  onChange={r => { setRows(r); setErrors(x => ({ ...x, bands: '' })) }}
                  error={errors.bands}
                  title="RushMyFood catering delivery fee"
                  intro={<>What a catering delivery costs when <strong>RushMyFood drivers</strong> bring it, by driving distance from the restaurant.</>}
                  empty={<><strong>No tiers</strong> = the regular platform delivery tiers are used.</>}
                  notes={[
                    <><strong>No tiers</strong> = the regular platform delivery tiers are used.</>,
                    <>When a restaurant&apos;s <strong>own drivers</strong> deliver catering, its own catering tiers apply instead (or its regular ones).</>
                  ]}
                />
              </Paper>

              {/* Save bar for the two cards above (sent together) */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, px: { xs: 0.5, sm: 1 } }}>
                <Typography role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: configDirty ? 700 : 500, color: configDirty ? CT.text : CT.secondary }}>
                  {configDirty
                    ? <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: CT.amber }} />
                    : <CheckRoundedIcon aria-hidden sx={{ fontSize: 18, color: CT.secondary }} />}
                  {configDirty ? 'Unsaved changes to the defaults or tiers' : 'Defaults and tiers saved'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.25 }}>
                  {configDirty && <Button variant="outlined" onClick={discardConfig} disabled={savingConfig}>Discard</Button>}
                  <Button variant="contained" onClick={saveConfig} disabled={!configDirty || savingConfig}
                    startIcon={savingConfig ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                    {savingConfig ? 'Saving…' : 'Save defaults and tiers'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Restaurants */}
          <Paper component="section" aria-labelledby="catering-restaurants" elevation={0} sx={{ ...CARD, mt: 1 }}>
            <Box sx={{ p: { xs: 2.5, sm: '28px 32px 20px' }, borderBottom: `1px solid ${CT.border}` }}>
              <SectionHead id="catering-restaurants" icon={<StorefrontOutlinedIcon />} title="Restaurants"
                intro={<>One restaurant&apos;s catering service fee and commission. Leave a box blank to use the <strong>platform catering default</strong> (shown in grey).</>} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
                <TextField size="small" placeholder="Search restaurants" value={search} onChange={e => setSearch(e.target.value)}
                  inputProps={{ 'aria-label': 'Search restaurants' }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 20, color: CT.secondary }} /></InputAdornment> }}
                  sx={{ flex: '1 1 260px', maxWidth: 380 }} />
                <Chip size="small" clickable aria-pressed={cateringOnly}
                  icon={<RoomServiceRoundedIcon sx={{ fontSize: 18 }} />}
                  label={`Catering on (${withCatering})`}
                  color={cateringOnly ? 'primary' : 'default'} variant={cateringOnly ? 'filled' : 'outlined'}
                  onClick={() => setCateringOnly(v => !v)}
                  sx={{ '& .MuiChip-icon': { color: cateringOnly ? '#FFFFFF' : CT.accent, ml: '10px' } }} />
                <Typography sx={{ fontSize: 14, color: CT.secondary, ml: { sm: 'auto' }, fontVariantNumeric: 'tabular-nums' }}>
                  {visibleRows.length} of {feeRows.length} restaurants
                </Typography>
              </Box>
            </Box>

            {feesError && <Alert severity="error" sx={{ m: 3 }}>{feesError.message}</Alert>}
            {feesLoading && feeRows.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={26} /></Box>
            ) : visibleRows.length === 0 ? (
              <Typography sx={{ py: 6, textAlign: 'center', color: CT.secondary, fontSize: 15 }}>
                {feeRows.length === 0 ? 'No active restaurants.' : 'No restaurants match.'}
              </Typography>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {visibleRows.map(r => (
                  <RestaurantRow key={r.restaurantId} row={r} config={platform ? {
                    serviceFee: platform.serviceFee || null,
                    deliveryCommissionRate: platform.deliveryCommissionRate || null,
                    selfDeliveryCommissionRate: platform.selfDeliveryCommissionRate || null,
                    pickupCommissionRate: platform.pickupCommissionRate || null
                  } : null} onSaved={onRowSaved} />
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
          <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  )
}
